import { Button, Space } from "antd";
import moment from "moment";

// ux2(A/presets): shared six-window date-range presets (Today / Yesterday /
// Last 7 days / Last 30 days / This month / Last month), reused everywhere a
// start/end date pair exists. antd 4.23.6 -> RangePicker prop is `ranges`
// (not `presets`), so native RangePickers consume getPresetRanges() directly
// while form-based start/end pairs use the <DateRangePresets /> button row.
// Every value here is a moment() instance -- never a plain Date/string.
// ux3(final): every value -- start AND end -- is .startOf("day") (whole-branch review I-1,
// replacing ux2(final-2)'s endOf("day") ends). A hand-picked antd DatePicker value is always
// local midnight (.startOf("day")). Pages that use these presets forward the raw moment
// (runPreview / form.getFieldsValue()), which serialises as an ISO UTC string; the backend
// (APP_TIMEZONE=Asia/Dhaka) then formats it with PHP's date('Y-m-d', strtotime(...)). An
// endOf("day") end -- e.g. 2026-09-16T22:59:59.999Z from a London (BST, UTC+1) browser -- lands
// on 2026-09-17 in Dhaka, one day past the label: "Last 7 days" silently became eight days,
// "This month" spilled into day 1 of next month, and the same dates hand-picked in the picker
// (midnight -> ...T23:00:00.000Z -> still 16-Sep) disagreed with the preset's own answer. Using
// startOf("day") for the end too makes every preset send the same midnight convention as a
// hand-picked value, so preset and hand-picked ranges parse on the same Dhaka date.
export const getPresetRanges = () => ({
  Today: [moment().startOf("day"), moment().startOf("day")],
  Yesterday: [
    moment().subtract(1, "days").startOf("day"),
    moment().subtract(1, "days").startOf("day"),
  ],
  "Last 7 days": [moment().subtract(6, "days").startOf("day"), moment().startOf("day")],
  "Last 30 days": [moment().subtract(29, "days").startOf("day"), moment().startOf("day")],
  "This month": [moment().startOf("month"), moment().startOf("day")],
  "Last month": [
    moment().subtract(1, "months").startOf("month"),
    moment().subtract(1, "months").endOf("month").startOf("day"),
  ],
});

/**
 * DateRangePresets
 * Props:
 *  - form: the antd Form instance that owns startName/endName
 *  - startName / endName: the Form.Item `name`s to set (must match the
 *    round-1 seeded `initialValue`s exactly)
 *  - onApply: called after the fields are set; defaults to form.submit(),
 *    i.e. the same submit path the Search button's htmlType="submit" uses
 *
 * ux8(T1): endName is optional -- single-date mode. Without it the row
 * renders only Today and Yesterday (the first two getPresetRanges() labels)
 * and applyPreset sets only startName, leaving whatever the field already
 * held for anything else untouched. This needs no new date math: Today and
 * Yesterday's start and end are already the same startOf("day") moment (see
 * the ux3(final) note above), so the same midnight convention that keeps a
 * range preset lined up with a hand-picked value applies unchanged when only
 * one field is being set.
 */
const DateRangePresets = ({ form, startName, endName, onApply }) => {
  if (!form || !startName) {
    return null;
  }

  const applyPreset = (start, end) => {
    const fields = { [startName]: start };
    if (endName) {
      fields[endName] = end;
    }
    form.setFieldsValue(fields);
    if (onApply) {
      onApply();
    } else {
      form.submit();
    }
  };

  const ranges = getPresetRanges();
  const labels = endName ? Object.keys(ranges) : ["Today", "Yesterday"];

  return (
    <Space size={4} wrap style={{ marginBottom: 8 }}>
      {labels.map((label) => (
        <Button
          key={label}
          size="small"
          onClick={() => applyPreset(ranges[label][0], ranges[label][1])}
        >
          {label}
        </Button>
      ))}
    </Space>
  );
};

export default DateRangePresets;
